import { Input } from '@thanx/uikit/input'
import { Radio } from '@thanx/uikit/radio'
import { Text } from '@thanx/uikit/text'
import { useStyletron } from '@thanx/uikit/theme'
import Section from 'components/Section'
import { buildTranslate } from 'locales'
import type { AddressError, ReplyMode, SectionState } from './helpers'

const t = buildTranslate('cms.settings.email_reply_routing')

type Props = {
  /** Section heading, e.g. "Marketing email replies go to:". */
  title: string
  /** Stable prefix for element ids + test ids, e.g. "marketing". */
  idPrefix: string
  state: SectionState
  onModeChange: (mode: ReplyMode) => void
  onAddressChange: (value: string) => void
  onAddressBlur: () => void
  error: AddressError
}

function ReplySection({
  title,
  idPrefix,
  state,
  onModeChange,
  onAddressChange,
  onAddressBlur,
  error,
}: Props) {
  const [css] = useStyletron()
  const groupName = `${idPrefix}-mode`
  const headingId = `${idPrefix}-heading`
  const errorId = `${idPrefix}-address-error`
  const errorMessage = error ? t(`address_${error}`) : ''

  // Standalone Radios (not baseui RadioGroup): the group clones every child as
  // a radio, which leaks radio props onto the conditional address field. This
  // also lets the address input sit directly under its option per the sketch.
  const renderRadio = (mode: ReplyMode, label: string) => (
    <div className="mb-xs">
      <Radio
        name={groupName}
        value={mode}
        checked={state.mode === mode}
        onChange={() => onModeChange(mode)}
      >
        {label}
      </Radio>
    </div>
  )

  return (
    <Section className="p-m mb-m">
      {/* role="group" + aria-labelledby names the radio group for assistive
          tech (the native `name` already provides radio semantics). A generic
          group, not role="radiogroup", because the conditional address input
          is interleaved among the options. */}
      <div role="group" aria-labelledby={headingId}>
        <h4 id={headingId} className="extra-bold mb-s">
          {title}
        </h4>
        {renderRadio('no_reply', t('option_no_reply'))}
        {state.mode === 'no_reply' && (
          <Text.BodyText5 tag="div" color="grey70" className="ml-l mb-s">
            {t('no_reply_note')}
          </Text.BodyText5>
        )}
        {renderRadio('address', t('option_address'))}
        {state.mode === 'address' && (
          <div className="ml-l mb-s">
            <Input
              type="email"
              value={state.address}
              onChange={onAddressChange}
              onBlur={onAddressBlur}
              placeholder={t('address_placeholder')}
              errored={!!error}
              className={css({ maxWidth: '400px' })}
              overrides={{
                Input: {
                  props: {
                    id: `${idPrefix}-address`,
                    // Specific field purpose; the section heading already names
                    // the channel via the role="group" aria-labelledby above.
                    'aria-label': t('address_aria_label'),
                    // The field only renders in address mode, where an address
                    // is required (blank submit is blocked with an error).
                    'aria-required': true,
                    'aria-invalid': !!error,
                    'aria-describedby': errorId,
                    'data-testid': `${idPrefix}-address`,
                  },
                },
              }}
            />
            <div id={errorId} role="alert">
              {error && (
                <Text.BodyText5 tag="div" color="cayenne50" className="mt-xxs">
                  {errorMessage}
                </Text.BodyText5>
              )}
            </div>
          </div>
        )}
      </div>
    </Section>
  )
}

export default ReplySection
